import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShellChrome } from '../hooks/useShellChrome';
import { loginCancelDestination } from '../utils/authRedirect';
import { LoginModal } from './LoginModal';

export default function LoginRouteModal() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [authInitialized, setAuthInitialized] = useState(!authLoading);
  const favoriteIntent = (location.state as { favoriteIntent?: number } | null)?.favoriteIntent;
  const returnState = Number.isSafeInteger(favoriteIntent) && Number(favoriteIntent) > 0 ? { favoriteIntent } : undefined;
  const requestedReturn = (location.state as { from?: string } | null)?.from;
  const returnTo = typeof requestedReturn === 'string' && requestedReturn.startsWith('/') && !requestedReturn.startsWith('//') && !requestedReturn.includes('\\') ? requestedReturn : '/';

  // Hide native Scan FAB while the login sheet is up (and restore on leave).
  useShellChrome({ hideScan: true });

  const handleClose = () => {
    setOpen(false);
    const dest = loginCancelDestination(returnTo);
    navigate(`${dest.pathname}${dest.search}`, {
      replace: true,
      state: { authPromptDismissed: true },
    });
  };

  const handleLogin = () => {
    setOpen(false);
    navigate(returnTo === '/login' ? '/' : returnTo, { state: returnState });
  };

  useEffect(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!authLoading) setAuthInitialized(true);
  }, [authLoading]);

  // Already signed in (e.g. native bootstrap race bounced here) — never flash login.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const dest = returnTo === '/login' || returnTo === '/register' ? '/' : returnTo;
    navigate(dest, { replace: true, state: Number.isSafeInteger(favoriteIntent) && Number(favoriteIntent) > 0 ? { favoriteIntent } : undefined });
  }, [authLoading, isAuthenticated, navigate, returnTo, favoriteIntent]);

  // Only hide during initial session lookup. Unmounting during login loses
  // the form's pending state and any rejection message when the request finishes.
  if (!authInitialized || isAuthenticated) {
    return null;
  }

  return (
    <LoginModal
      isOpen={open}
      onClose={handleClose}
      onLogin={handleLogin}
      onSwitchToRegister={() => navigate('/register', { state: { from: returnTo, ...returnState } })}
    />
  );
}
